import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InputGroup, Spinner } from '@blueprintjs/core';
import { toast } from 'sonner';
import { fetchHolds, resolveHold, type HoldCandidate, type HoldRow } from './api';
import { useRecordNavigator } from '../../hooks/useRecordNavigator';
import { leadImportKeys } from './keys';
import { queryPolicies } from '../../utils/queryPolicies';
import { Card, Empty, ErrorNote, Pill, Segmented, fmtDay, fmtInt, fmtWhen } from './ui';

const KIND_LABEL: Record<string, string> = {
  competing_phone_merchants: 'Phone matches several merchants',
  group_link_conflict: 'Earlier copies are linked to a different merchant',
  lookup_no_longer_matches: 'Linked merchant no longer has this phone',
};

/** Review queue for merchant links. An issue never blocks the lead or the batch. */
export default function HoldQueue({ batchId, onOpenLead }: { batchId: number | null; onOpenLead: (id: number) => void }) {
  const [status, setStatus] = useState<'open' | 'resolved' | 'dismissed'>('open');
  const q = useQuery({ queryKey: leadImportKeys.holds(status, batchId), queryFn: () => fetchHolds(status, batchId), ...queryPolicies.pagedData });
  const rows = q.data ?? [];
  return (
    <Card title="Needs review" subtitle="These leads match more than one merchant, or a different one than before. Pick the right merchant to make them assignable." actions={
      <Segmented ariaLabel="Hold status" value={status} onChange={setStatus}
        options={[{ value: 'open', label: 'Open' }, { value: 'resolved', label: 'Resolved' }, { value: 'dismissed', label: 'Dismissed' }]} />
    } flush>
      <ErrorNote error={q.error} />
      {q.isLoading ? <div className="li-card__body"><Spinner size={20} /></div> : rows.length === 0 ? <Empty>{status === 'open' ? 'Nothing needs review.' : `No ${status} items.`}</Empty> : (
        <div className="li-tablewrap">
          <table className="li-table">
            <thead><tr><th>Lead</th><th>Issue</th><th>Possible merchants</th><th>Import</th><th>Raised</th><th>{status === 'open' ? 'Decide' : 'Decision'}</th></tr></thead>
            <tbody>{rows.map((h) => <HoldRowView key={h.id} h={h} onOpenLead={onOpenLead} />)}</tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function HoldRowView({ h, onOpenLead }: { h: HoldRow; onOpenLead: (id: number) => void }) {
  const queryClient = useQueryClient();
  const { openRecord } = useRecordNavigator();
  const [note, setNote] = useState('');
  const resolve = useMutation({
    mutationFn: ({ action, merchant }: { action: 'link' | 'keep_unlinked' | 'dismiss'; merchant?: number }) =>
      resolveHold(h.id, action, merchant ?? null, note || null),
    onSuccess: () => { toast.success('Resolved.'); queryClient.invalidateQueries({ queryKey: leadImportKeys.all }); },
    onError: (e) => toast.error((e as Error).message),
  });
  const visible = h.candidates.filter((c): c is HoldCandidate & { id: number } => c.visible && c.id != null);
  const openMerchant = (c: { id: number; label: string }) =>
    openRecord({ tableName: 'merchant', id: c.id, label: c.label || `Merchant #${c.id}` });
  // The side panel activates the last tab opened, so open in reverse to leave
  // the first candidate in front.
  const openAll = () => [...visible].reverse().forEach(openMerchant);
  const open = h.status === 'open';
  return (
    <tr>
      <td><button type="button" className="li-btn li-btn--link" onClick={() => onOpenLead(h.lead)}>{h.business_name || h.phone}</button><div className="li-muted">{h.phone_normalized}</div></td>
      <td>{KIND_LABEL[h.kind] ?? h.kind}</td>
      <td>
        <ul className="li-candidates" aria-label="Possible merchants">
          {h.candidates.map((c, i) => (
            <li key={c.id ?? `restricted-${i}`} className="li-candidate">
              {c.visible && c.id != null ? (
                <>
                  <div className="li-candidate__head">
                    <button type="button" className="li-btn li-btn--link" title="Open in the side panel"
                      onClick={() => openMerchant({ id: c.id as number, label: c.label })}>
                      #{c.id} {c.label || 'Unnamed merchant'}
                    </button>
                    {open && (
                      <Button small intent="primary" loading={resolve.isPending}
                        onClick={() => resolve.mutate({ action: 'link', merchant: c.id as number })}>Link</Button>
                    )}
                  </div>
                  <CandidateReasons c={c} />
                </>
              ) : <Pill tone="muted">{c.label}</Pill>}
            </li>
          ))}
        </ul>
        {visible.length > 1 && (
          <button type="button" className="li-btn li-btn--quiet li-candidates__all" onClick={openAll}>
            Open all {visible.length} in the side panel
          </button>
        )}
      </td>
      <td>{h.source}, {fmtDay(h.batch_date)}</td>
      <td>{fmtWhen(h.created_at)}</td>
      <td>
        {open ? (
          <div className="li-resolve">
            <InputGroup small value={note} placeholder="Note (optional)" onChange={(e) => setNote(e.target.value)} />
            <div>
              <Button small minimal loading={resolve.isPending} onClick={() => resolve.mutate({ action: 'keep_unlinked' })}>Keep unlinked</Button>
              <Button small minimal loading={resolve.isPending} onClick={() => resolve.mutate({ action: 'dismiss' })}>Dismiss</Button>
            </div>
          </div>
        ) : <span>{h.resolution ? h.resolution.charAt(0).toUpperCase() + h.resolution.slice(1).replace(/_/g, ' ') : '-'}{h.note ? `: ${h.note}` : ''}</span>}
      </td>
    </tr>
  );
}

/** Why a merchant is a candidate, and the facts that tell candidates apart. */
function CandidateReasons({ c }: { c: HoldCandidate }) {
  const matches = c.phone_matches ?? [];
  const facts = [
    c.status && `Status ${c.status}`,
    c.owner && `Owner ${c.owner}`,
    c.submissions != null && `${fmtInt(c.submissions)} submission${c.submissions === 1 ? '' : 's'}`,
    c.created_at && `Created ${fmtDay(c.created_at.slice(0, 10))}`,
  ].filter(Boolean);
  return (
    <div className="li-candidate__why">
      {matches.map((m) => <div key={m.field}>Has this number as {m.field}: <strong>{m.value}</strong></div>)}
      {!!c.earlier_imports && <div>Linked by {fmtInt(c.earlier_imports)} lead{c.earlier_imports === 1 ? '' : 's'} from earlier imports</div>}
      {matches.length === 0 && !c.earlier_imports && <div>None of its phone fields has this number now</div>}
      {facts.length > 0 && <div className="li-muted">{facts.join(', ')}</div>}
    </div>
  );
}
